/**
 * /admin — user accounts and projects.
 *
 * ⛔ The gate is `RequireAdmin` HERE **and** `requireAdmin` on every endpoint in the API,
 *   independently. Hiding the nav entry is not access control; see RequireAdmin.tsx.
 *
 * ⭐ Two panels behind one tab control rather than two routes. They are the same job ("set the system
 *   up"), an admin moves between them constantly, and a second route would mean a second place to
 *   remember the admin gate.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminProjectsPanel } from './AdminProjectsPanel';
import { AdminUsersPanel } from './AdminUsersPanel';
import { RequireAdmin } from './RequireAdmin';

type Panel = 'users' | 'projects';

const TABS: ReadonlyArray<{ id: Panel; label: string }> = [
  { id: 'users', label: 'Users' },
  { id: 'projects', label: 'Projects' },
];

export function AdminPage() {
  const [panel, setPanel] = useState<Panel>('users');

  return (
    <RequireAdmin>
      <div className="mx-auto w-full max-w-3xl space-y-4 px-3 py-4">
        <header>
          <h1 className="text-lg font-bold tracking-tight">Administration</h1>
          <p className="mt-0.5 text-sm text-[color:var(--color-ink-muted)]">
            Accounts and projects. Every change here is recorded in the audit log with your name on it.
          </p>
        </header>

        {/* ⭐ Real buttons with `aria-current`, not styled divs — this is the only navigation on the
            screen and a keyboard user has to be able to reach it. */}
        <nav className="flex gap-1 rounded-md border border-[color:var(--color-line)] p-1" aria-label="Admin sections">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              aria-current={panel === tab.id ? 'page' : undefined}
              onClick={() => setPanel(tab.id)}
              className={`h-9 flex-1 rounded text-sm font-medium ${
                panel === tab.id
                  ? 'bg-[color:var(--color-accent)] text-[color:var(--color-on-accent)]'
                  : 'text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-surface)]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {/**
         * 🔴 THE ENTRANCE TO `/settings` (PH-26). It is a `Link`, not a third panel: settings is a
         *   real route with its own `?tab=`, its own suite, and READS that any signed-in user may
         *   make — folding it in here would put open data behind `RequireAdmin`.
         *
         * ⭐⭐ WHY HERE AND NOT IN `views.ts`. Every write on that screen is `requireAdmin` on the
         *   API, and `views.ts` drives the desktop tab row AND the 390px bottom bar — surfaces every
         *   signed-in user sees. A tab leading most of the team to a screen where each save 403s is
         *   worse than no tab, which is the judgement `/admin` itself already carries.
         * ⛔ AND NOT A SECOND TOP-BAR ICON: `TopBar`'s 390px budget is measured at ~330 of 366
         *   usable pixels with the ⚙ already in it. Another 32px control would not fit, and the thing
         *   it pushed off would be `Sign out`.
         */}
        <Link
          to="/settings"
          className="flex items-center gap-3 rounded-md border border-[color:var(--color-line)] p-3 hover:bg-black/5 dark:hover:bg-white/10"
        >
          <span aria-hidden="true" className="text-lg">
            ⚑
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold">Settings — the shared vocabularies</span>
            <span className="block text-xs text-[color:var(--color-ink-muted)]">
              Statuses, types, priorities, impacts, resolutions and the transition matrix. ⚠️ These
              are global: one set for every project, so a relabel here changes every ticket's chip.
            </span>
          </span>
          <span aria-hidden="true" className="text-[color:var(--color-ink-muted)]">
            →
          </span>
        </Link>

        {panel === 'users' ? <AdminUsersPanel /> : <AdminProjectsPanel />}
      </div>
    </RequireAdmin>
  );
}

export default AdminPage;
